/**
 * @file AddEditImportTemplateForm.js
 * @description Form component for adding or editing an import template.
 */

import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    getImportTemplate,
    getImportTypes,
} from '../../services/importService';
import DefineFileTab from './DefineFileTab';
import styles from './ImportTemplates.module.scss';
import Spinner from '../ui/spinners/Spinner';
import DefineFieldsTab from './DefineFieldsTab';
import DefineMappingTab from './DefineMappingTab';
import DefineImportTypesTab from './DefineImportTypesTab';
import Toast from '../ui/toasts/Toast';
import {
    ImportFileFormat,
    ImportDelimiter,
    SecurityAttributes,
    ImportType,
} from '../../utils/data/enums';
import { useAuthContext } from '../../store/Contexts';
import { getText } from '../../i18n';

const TABS = {
    DEFINE_FILE: 'DEFINE_FILE',
    DEFINE_IMPORT_TYPES: 'DEFINE_IMPORT_TYPES',
    DEFINE_FIELDS: 'DEFINE_FIELDS',
    DEFINE_MAPPING: 'DEFINE_MAPPING',
};

const TAB_LABELS = {
    [TABS.DEFINE_FILE]: getText('msg_d8b59e72d105'),
    [TABS.DEFINE_IMPORT_TYPES]: getText('msg_3a5b077111a3'),
    [TABS.DEFINE_FIELDS]: getText('msg_b4f2c16f90df'),
    [TABS.DEFINE_MAPPING]: getText('msg_569b12ab04ec'),
};

const createBlankTemplateType = (processingOrder = 1) => ({
    id: 0,
    importTemplateId: 0,
    importTypeId: null,
    name: '',
    processingOrder,
    templateFields: [],
    importMapGroups: [],
});

const getOrderedTemplateTypes = (templateTypes = []) =>
    [...templateTypes].sort(
        (a, b) => (a.processingOrder ?? 0) - (b.processingOrder ?? 0),
    );

const isColumnBasedFormat = importFileFormatId =>
    importFileFormatId === ImportFileFormat.Delimited ||
    importFileFormatId === ImportFileFormat.Excel;

const normalizeTemplateFieldForState = (
    templateField = {},
    templateId = 0,
    templateTypeId = 0,
    index = 0,
    importFileFormatId = 0,
) => ({
    id: templateField.id ?? 0,
    importTemplateId: templateField.importTemplateId ?? templateId,
    importTemplateTypeId: templateField.importTemplateTypeId ?? templateTypeId,
    importFieldId: templateField.importFieldId ?? null,
    importFieldName: templateField.importFieldName ?? '',
    columnNumber: isColumnBasedFormat(importFileFormatId)
        ? (templateField.columnNumber ?? index + 1)
        : (templateField.columnNumber ?? 0),
    headerName: templateField.headerName ?? '',
    startingLocation: templateField.startingLocation ?? 0,
    length: templateField.length ?? 0,
});

const buildTemplateFieldPayload = (
    templateField = {},
    templateId = 0,
    templateTypeId = 0,
    index = 0,
    importFileFormatId = 0,
) => {
    const normalizedField = normalizeTemplateFieldForState(
        templateField,
        templateId,
        templateTypeId,
        index,
        importFileFormatId,
    );

    return {
        id: normalizedField.id,
        importTemplateId: templateId,
        importTemplateTypeId: templateTypeId,
        importFieldId: normalizedField.importFieldId,
        columnNumber: normalizedField.columnNumber ?? 0,
        startingLocation: normalizedField.startingLocation ?? 0,
        length: normalizedField.length ?? 0,
    };
};

const normalizeTranslationMapForState = (translationMap = {}, index = 0) => ({
    id: translationMap.id ?? 0,
    processingOrder: translationMap.processingOrder ?? index + 1,
    from: translationMap.else ? null : (translationMap.from ?? ''),
    to: translationMap.to ?? '',
    else: !!translationMap.else,
});

const buildTranslationMapPayload = (translationMap = {}, index = 0) => {
    const normalizedTranslationMap = normalizeTranslationMapForState(
        translationMap,
        index,
    );

    return {
        id: normalizedTranslationMap.id,
        processingOrder: normalizedTranslationMap.processingOrder,
        from: normalizedTranslationMap.from,
        to: normalizedTranslationMap.to,
        else: normalizedTranslationMap.else,
    };
};

const normalizeFormulaMapEvaluationForState = (evaluation = {}) => ({
    id: evaluation.id ?? 0,
    leftExpression: evaluation.leftExpression ?? '',
    rightExpression: evaluation.rightExpression ?? '',
    operatorId: evaluation.operatorId ?? null,
});

const buildFormulaMapEvaluationPayload = evaluation => {
    const normalizedEvaluation =
        normalizeFormulaMapEvaluationForState(evaluation);

    return {
        id: normalizedEvaluation.id,
        leftExpression: normalizedEvaluation.leftExpression,
        rightExpression: normalizedEvaluation.rightExpression,
        operatorId: normalizedEvaluation.operatorId,
    };
};

const normalizeFormulaMapForState = formulaMap => {
    if (!formulaMap) {
        return null;
    }

    return {
        id: formulaMap.id ?? 0,
        value: formulaMap.value ?? '',
        evaluations: (formulaMap.evaluations || []).map(evaluation =>
            normalizeFormulaMapEvaluationForState(evaluation),
        ),
    };
};

const buildFormulaMapPayload = formulaMap => {
    const normalizedFormulaMap = normalizeFormulaMapForState(formulaMap);

    if (!normalizedFormulaMap) {
        return null;
    }

    return {
        id: normalizedFormulaMap.id,
        value: normalizedFormulaMap.value,
        evaluations: normalizedFormulaMap.evaluations.map(evaluation =>
            buildFormulaMapEvaluationPayload(evaluation),
        ),
    };
};

const normalizeImportMapGroupForState = (
    importMapGroup = {},
    templateId = 0,
    templateTypeId = 0,
    index = 0,
) => {
    const groupTypeId = importMapGroup.importMapGroupTypeId ?? 1;

    return {
        id: importMapGroup.id ?? 0,
        importTemplateId: importMapGroup.importTemplateId ?? templateId,
        importTemplateTypeId:
            importMapGroup.importTemplateTypeId ?? templateTypeId,
        importMapGroupTypeId: groupTypeId,
        processingOrder: importMapGroup.processingOrder ?? index + 1,
        importFieldId: importMapGroup.importFieldId ?? null,
        importFieldName: importMapGroup.importFieldName ?? '',
        errorMessage: importMapGroup.errorMessage ?? '',
        warningMessage: importMapGroup.warningMessage ?? '',
        translationMaps:
            groupTypeId === 1
                ? (importMapGroup.translationMaps || []).map(
                      (translationMap, translationIndex) =>
                          normalizeTranslationMapForState(
                              translationMap,
                              translationIndex,
                          ),
                  )
                : [],
        formulaMap:
            groupTypeId === 2
                ? (normalizeFormulaMapForState(importMapGroup.formulaMap) ?? {
                      id: 0,
                      value: '',
                      evaluations: [],
                  })
                : null,
        selected: !!importMapGroup.selected,
        isOpen: !!importMapGroup.isOpen,
    };
};

const buildImportMapGroupPayload = (
    importMapGroup = {},
    templateId = 0,
    templateTypeId = 0,
    index = 0,
) => {
    const normalizedGroup = normalizeImportMapGroupForState(
        importMapGroup,
        templateId,
        templateTypeId,
        index,
    );

    return {
        id: normalizedGroup.id,
        importMapGroupTypeId: normalizedGroup.importMapGroupTypeId,
        importTemplateTypeId: templateTypeId,
        importTemplateId: templateId,
        processingOrder: normalizedGroup.processingOrder,
        errorMessage: normalizedGroup.errorMessage,
        warningMessage: normalizedGroup.warningMessage,
        importFieldId: normalizedGroup.importFieldId ?? 0,
        translationMaps: normalizedGroup.translationMaps.map(
            (translationMap, translationIndex) =>
                buildTranslationMapPayload(translationMap, translationIndex),
        ),
        formulaMap: buildFormulaMapPayload(normalizedGroup.formulaMap),
    };
};

const normalizeTemplateTypeForState = (
    templateType = {},
    rawTemplate = {},
    importTypes = [],
    index = 0,
) => {
    const templateId = rawTemplate?.id ?? 0;
    const templateTypeId = templateType.id ?? 0;
    const importFileFormatId = rawTemplate?.importFileFormatId ?? 0;

    const matchingType = importTypes.find(
        type => type.id === templateType.importTypeId,
    );

    return {
        id: templateTypeId,
        importTemplateId: templateType.importTemplateId ?? templateId,
        importTypeId: templateType.importTypeId ?? null,
        importTypeName: matchingType?.name || templateType.importTypeName || '',
        name: templateType.name ?? '',
        processingOrder: templateType.processingOrder ?? index + 1,
        templateFields: (templateType.templateFields || []).map(
            (templateField, fieldIndex) =>
                normalizeTemplateFieldForState(
                    templateField,
                    templateId,
                    templateTypeId,
                    fieldIndex,
                    importFileFormatId,
                ),
        ),
        importMapGroups: (templateType.importMapGroups || []).map(
            (importMapGroup, mapGroupIndex) =>
                normalizeImportMapGroupForState(
                    importMapGroup,
                    templateId,
                    templateTypeId,
                    mapGroupIndex,
                ),
        ),
    };
};

const buildTemplateTypePayload = (
    templateType = {},
    templateId = 0,
    importFileFormatId = 0,
    index = 0,
) => {
    const templateTypeId = templateType.id ?? 0;

    return {
        id: templateTypeId,
        importTemplateId: templateId,
        importTypeId: templateType.importTypeId ?? 0,
        name: (templateType.name || '').trim(),
        processingOrder: index + 1,
        templateFields: (templateType.templateFields || []).map(
            (templateField, fieldIndex) =>
                buildTemplateFieldPayload(
                    templateField,
                    templateId,
                    templateTypeId,
                    fieldIndex,
                    importFileFormatId,
                ),
        ),
        importMapGroups: (templateType.importMapGroups || []).map(
            (importMapGroup, mapGroupIndex) =>
                buildImportMapGroupPayload(
                    importMapGroup,
                    templateId,
                    templateTypeId,
                    mapGroupIndex,
                ),
        ),
    };
};

const getFirstTemplateType = templateData =>
    getOrderedTemplateTypes(templateData?.templateTypes || [])[0] || null;

const calculateColumnPositionCount = templateData => {
    const firstTemplateType = getFirstTemplateType(templateData);
    const templateFields = firstTemplateType?.templateFields || [];

    if (!templateFields.length) return 0;

    if (templateData.importFileFormatId === ImportFileFormat.FixedLength) {
        let maxEndingPosition = 0;

        templateFields.forEach(field => {
            const endingPosition =
                (field.startingLocation ?? 0) + (field.length ?? 0);
            if (endingPosition > maxEndingPosition) {
                maxEndingPosition = endingPosition;
            }
        });

        return maxEndingPosition;
    }

    if (
        templateData.importFileFormatId === ImportFileFormat.Excel ||
        templateData.importFileFormatId === ImportFileFormat.Delimited
    ) {
        return templateFields.length;
    }

    return 0;
};

const normalizeTemplateData = (rawTemplate, importTypes = []) => {
    const normalized = {
        id: rawTemplate?.id ?? 0,
        tenantId: rawTemplate?.tenantId ?? 0,
        name: rawTemplate?.name ?? '',
        customDelimiter: rawTemplate?.customDelimiter ?? '',
        hasHeaderRecord: rawTemplate?.hasHeaderRecord ?? false,
        importDelimiterId: rawTemplate?.importDelimiterId ?? null,
        importDelimiterName: rawTemplate?.importDelimiterName ?? '',
        importFileFormatId: rawTemplate?.importFileFormatId ?? 0,
        importFileFormatName: rawTemplate?.importFileFormatName ?? '',
        hasTextQualifier: rawTemplate?.hasTextQualifier ?? false,
        sheetName: rawTemplate?.sheetName ?? '',
        importTypeId: rawTemplate?.importTypeId ?? null,
        templateTypes:
            rawTemplate?.templateTypes?.length > 0
                ? getOrderedTemplateTypes(rawTemplate.templateTypes).map(
                      (templateType, index) =>
                          normalizeTemplateTypeForState(
                              templateType,
                              rawTemplate,
                              importTypes,
                              index,
                          ),
                  )
                : [
                      {
                          ...createBlankTemplateType(1),
                          importTemplateId: rawTemplate?.id ?? 0,
                      },
                  ],
    };

    return {
        ...normalized,
        columnPositionCount: calculateColumnPositionCount(normalized),
    };
};

const isTemplateTypeCompletelyBlank = templateType => {
    const hasImportType = !!templateType?.importTypeId;
    const hasName = !!(templateType?.name || '').trim();
    const hasFields = (templateType?.templateFields || []).some(
        field =>
            field.importFieldId ||
            field.importFieldName ||
            field.headerName ||
            field.startingLocation ||
            field.length,
    );
    const hasMappings = (templateType?.importMapGroups || []).length > 0;

    return !hasImportType && !hasName && !hasFields && !hasMappings;
};

const isTemplateTypePartiallyCompleted = templateType => {
    const hasImportType = !!templateType?.importTypeId;
    const hasName = !!(templateType?.name || '').trim();

    return (hasImportType && !hasName) || (!hasImportType && hasName);
};

const getSanitizedTemplateTypes = (templateTypes = []) =>
    getOrderedTemplateTypes(templateTypes)
        .filter(templateType => !isTemplateTypeCompletelyBlank(templateType))
        .map((templateType, index) => ({
            ...templateType,
            name: (templateType.name || '').trim(),
            processingOrder: index + 1,
        }));

export const syncTemplateDataWithSavedTemplate = (
    templateData = {},
    savedTemplate = {},
    importTypes = [],
) => {
    const templateTypes = templateData?.templateTypes || [];
    const savedTemplateTypes = savedTemplate?.templateTypes || [];

    if (!savedTemplate || !Object.keys(savedTemplate).length) {
        return normalizeTemplateData(templateData, importTypes);
    }

    const mergedTemplateTypes =
        savedTemplateTypes.length > 0
            ? getOrderedTemplateTypes(templateTypes).map(
                  (templateType, index) => {
                      const savedTemplateType =
                          getOrderedTemplateTypes(savedTemplateTypes)[index];

                      if (!savedTemplateType) {
                          return templateType;
                      }

                      return {
                          ...templateType,
                          ...savedTemplateType,
                          id: savedTemplateType.id ?? templateType.id ?? 0,
                          importTemplateId:
                              savedTemplateType.importTemplateId ??
                              templateType.importTemplateId ??
                              templateData?.id ??
                              0,
                          importTypeId:
                              savedTemplateType.importTypeId ??
                              templateType.importTypeId ??
                              null,
                          name:
                              savedTemplateType.name ?? templateType.name ?? '',
                          processingOrder:
                              savedTemplateType.processingOrder ??
                              templateType.processingOrder ??
                              index + 1,
                          templateFields:
                              savedTemplateType.templateFields ??
                              templateType.templateFields ??
                              [],
                          importMapGroups:
                              savedTemplateType.importMapGroups ??
                              templateType.importMapGroups ??
                              [],
                      };
                  },
              )
            : templateTypes;

    return normalizeTemplateData(
        {
            ...templateData,
            ...savedTemplate,
            templateTypes: mergedTemplateTypes,
        },
        importTypes,
    );
};

const buildTemplatePayload = (
    templateData,
    { preserveExistingRootImportTypeId = false } = {},
) => {
    const sanitizedTemplateTypes = getSanitizedTemplateTypes(
        templateData.templateTypes || [],
    );

    const templateTypesPayload = sanitizedTemplateTypes.map(
        (templateType, index) =>
            buildTemplateTypePayload(
                templateType,
                templateData.id ?? 0,
                templateData.importFileFormatId ?? 0,
                index,
            ),
    );

    const firstTemplateTypeImportTypeId =
        templateTypesPayload[0]?.importTypeId ?? null;

    return {
        id: templateData.id ?? 0,
        tenantId: templateData.tenantId ?? 0,
        name: (templateData.name || '').trim(),
        customDelimiter: templateData.customDelimiter ?? '',
        hasHeaderRecord: !!templateData.hasHeaderRecord,
        importDelimiterId: templateData.importDelimiterId ?? null,
        importFileFormatId: templateData.importFileFormatId ?? 0,
        hasTextQualifier: !!templateData.hasTextQualifier,
        sheetName: templateData.sheetName ?? '',
        importTypeId: preserveExistingRootImportTypeId
            ? (templateData.importTypeId ?? firstTemplateTypeImportTypeId)
            : firstTemplateTypeImportTypeId,
        templateTypes: templateTypesPayload,
    };
};

const getImportTypeEditorTitle = (
    templateData,
    selectedTemplateType,
    importTypes = [],
) => {
    const templateName = (templateData?.name || '').trim();

    if (!templateName) {
        return getText('msg_aaed86679f0b');
    }

    const nonBlankTemplateTypes = getSanitizedTemplateTypes(
        templateData?.templateTypes || [],
    );

    const matchedImportType = importTypes.find(
        type => type.id === selectedTemplateType?.importTypeId,
    );

    const resolvedImportTypeName =
        matchedImportType?.name ||
        selectedTemplateType?.importTypeName ||
        getText('msg_78cfb1f529c1');

    const resolvedTemplateTypeName =
        (selectedTemplateType?.name || '').trim() || resolvedImportTypeName;

    if (nonBlankTemplateTypes.length <= 1) {
        return `${templateName} - ${resolvedImportTypeName}`;
    }

    return `${templateName} - Multi (${resolvedTemplateTypeName})`;
};

const ALWAYS_COMPARISON_OPERATOR_ID = 3;

const AddEditImportTemplateForm = ({
    onAddEditTemplate,
    onReturnToList,
    templateId,
}) => {
    const [activeTab, setActiveTab] = useState(TABS.DEFINE_FILE);
    const [templateData, setTemplateData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [importTypes, setImportTypes] = useState([]);
    const [selectedTemplateTypeIndex, setSelectedTemplateTypeIndex] =
        useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { hasSecurityAttribute } = useAuthContext();

    const hasFullAccess = hasSecurityAttribute([
        SecurityAttributes.CustomImportsFullAccess,
    ]);

    const selectedTemplateType = useMemo(() => {
        const orderedTemplateTypes = getOrderedTemplateTypes(
            templateData?.templateTypes || [],
        );
        return (
            orderedTemplateTypes[selectedTemplateTypeIndex] ||
            orderedTemplateTypes[0] ||
            null
        );
    }, [templateData, selectedTemplateTypeIndex]);

    const isChildEditorTab = tab =>
        tab === TABS.DEFINE_FIELDS || tab === TABS.DEFINE_MAPPING;

    const isTopTabActive = tab => {
        if (tab === TABS.DEFINE_IMPORT_TYPES && isChildEditorTab(activeTab)) {
            return true;
        }

        return activeTab === tab;
    };

    const handleBackNavigation = () => {
        if (activeTab === TABS.DEFINE_IMPORT_TYPES) {
            setActiveTab(TABS.DEFINE_FILE);
            return;
        }

        if (isChildEditorTab(activeTab)) {
            setActiveTab(TABS.DEFINE_IMPORT_TYPES);
        }
    };

    async function initializeForm() {
        try {
            setIsLoading(true);
            setSelectedTemplateTypeIndex(0);

            const importTypesResponse = await getImportTypes();
            setImportTypes(importTypesResponse || []);

            if (templateId) {
                const data = await getImportTemplate(templateId);
                const normalizedData = normalizeTemplateData(
                    data,
                    importTypesResponse || [],
                );

                setTemplateData(normalizedData);
            } else {
                const initialTemplate = normalizeTemplateData(
                    {
                        templateTypes: [createBlankTemplateType(1)],
                    },
                    importTypesResponse || [],
                );

                setTemplateData(initialTemplate);
            }

            setError(null);
        } catch (error) {
            console.error('Error preparing import template form:', error);
            setError(getText('msg_e277f972cf2e'));
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        initializeForm();
    }, [templateId]);

    useEffect(() => {
        const orderedTemplateTypes = getOrderedTemplateTypes(
            templateData?.templateTypes || [],
        );

        if (!orderedTemplateTypes.length) {
            if (selectedTemplateTypeIndex !== 0) {
                setSelectedTemplateTypeIndex(0);
            }
            return;
        }

        const maxIndex = orderedTemplateTypes.length - 1;

        if (selectedTemplateTypeIndex > maxIndex) {
            setSelectedTemplateTypeIndex(maxIndex);
        }
    }, [templateData?.templateTypes, selectedTemplateTypeIndex]);

    const validateDefineFileTab = () => {
        const {
            name,
            importFileFormatId,
            importDelimiterId,
            customDelimiter,
            columnPositionCount,
        } = templateData;

        if (!name) {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }

        if (!importFileFormatId) {
            Toast.error(getText('msg_84afdb51211c'));
            return false;
        }

        if (importFileFormatId === ImportFileFormat.Delimited) {
            if (
                importDelimiterId === ImportDelimiter.Custom &&
                !(customDelimiter ?? '').length
            ) {
                Toast.error(getText('msg_a6a026be165a'));
                return false;
            } else if (!importDelimiterId || importDelimiterId <= 0) {
                Toast.error(getText('msg_1dd22f1a85fd'));
                return false;
            }
        }

        if (!columnPositionCount) {
            Toast.error(
                getText('msg_1a8f6f5f6a01', {
                    fieldName: getColumnPositionFieldName(),
                }),
            );
            return false;
        }

        if (isNaN(columnPositionCount)) {
            Toast.error(
                getText('msg_2b9c7e6d5a02', {
                    fieldName: getColumnPositionFieldName(),
                }),
            );
            return false;
        }

        if (columnPositionCount <= 0) {
            Toast.error(
                getText('msg_3cad8f7e6b03', {
                    fieldName: getColumnPositionFieldName(),
                }),
            );
            return false;
        }

        return true;
    };

    const getColumnPositionFieldName = () => {
        if (
            templateData.importFileFormatId === ImportFileFormat.Delimited ||
            templateData.importFileFormatId === ImportFileFormat.Excel
        ) {
            return getText('msg_44c6e77fb1ab');
        }

        return getText('msg_e8b6b5206a33');
    };

    const validateTemplateTypeFields = (templateType, index = null) => {
        const templateTypeLabel = getQuotedTemplateTypeLabel(
            templateType,
            index,
        );
        const templateFields = templateType?.templateFields || [];
        const definedFields = templateFields.filter(
            field => field.importFieldId,
        );

        if (!definedFields.length) {
            Toast.error(
                getText('msg_4dbe90af7c04', {
                    templateTypeLabel,
                }),
            );
            return false;
        }

        const importFieldIds = definedFields.map(field => field.importFieldId);
        const uniqueFieldIds = new Set(importFieldIds);

        if (importFieldIds.length !== uniqueFieldIds.size) {
            const repeatedFields = definedFields.reduce((acc, field) => {
                if (
                    importFieldIds.filter(id => id === field.importFieldId)
                        .length > 1
                ) {
                    acc[field.importFieldId] =
                        field.importFieldName ||
                        field.headerName ||
                        getText('msg_c326a4660b67');
                }
                return acc;
            }, {});

            Object.values(repeatedFields).forEach(fieldName => {
                Toast.error(
                    getText('msg_5ecfa1b08d15', {
                        fieldName,
                        templateTypeLabel,
                    }),
                );
            });

            return false;
        }

        if (
            templateData.importFileFormatId === ImportFileFormat.FixedLength &&
            definedFields.length > 0
        ) {
            const orderedDefinedFields = [...definedFields].sort(
                (a, b) => (a.startingLocation ?? 0) - (b.startingLocation ?? 0),
            );

            const lastDefinedField = orderedDefinedFields.at(-1);
            const lastFieldEndingPosition =
                (lastDefinedField?.startingLocation ?? 0) +
                (lastDefinedField?.length ?? 0);

            if (templateData.columnPositionCount !== lastFieldEndingPosition) {
                Toast.error(
                    getText('msg_6fd0b2c19e26', {
                        templateTypeLabel,
                        expectedEndingPosition:
                            templateData.columnPositionCount,
                        actualEndingPosition: lastFieldEndingPosition,
                    }),
                );
                return false;
            }
        }

        return true;
    };

    const validateDefineFieldsTab = () =>
        validateTemplateTypeFields(selectedTemplateType);

    const validateAllTemplateTypeFields = templateTypesToValidate => {
        for (let index = 0; index < templateTypesToValidate.length; index++) {
            if (
                !validateTemplateTypeFields(
                    templateTypesToValidate[index],
                    index,
                )
            ) {
                return false;
            }
        }

        return true;
    };

    const IMPORT_MAP_GROUP_TYPE = {
        TRANSLATION: 1,
        FORMULA: 2,
    };

    const getTemplateTypeDisplayName = (templateType, index = null) => {
        const customName = (templateType?.name || '').trim();
        const importTypeName = (templateType?.importTypeName || '').trim();

        if (customName) {
            return customName;
        }

        if (importTypeName) {
            return importTypeName;
        }

        if (index !== null) {
            return getText('msg_68acae463f96', { index: index + 1 });
        }

        return getText('msg_78cfb1f529c1');
    };

    const getQuotedTemplateTypeLabel = (templateType, index = null) =>
        `"${getTemplateTypeDisplayName(templateType, index)}"`;

    const getTrimmedValue = value => (value ?? '').toString().trim();

    const validateTranslationMapGroup = (
        group,
        templateTypeName,
        mapGroupIndex,
    ) => {
        const translationMaps = group?.translationMaps || [];
        const displayMapGroupIndex = mapGroupIndex + 1;

        if (!translationMaps.length) {
            Toast.error(
                getText('msg_70e1c3d2af37', {
                    mapGroupIndex: displayMapGroupIndex,
                    templateTypeName,
                }),
            );
            return false;
        }

        const invalidProcessingOrder = translationMaps.some(
            translationMap => (translationMap.processingOrder ?? 0) <= 0,
        );

        if (invalidProcessingOrder) {
            Toast.error(
                getText('msg_81f2d4e3b048', {
                    mapGroupIndex: displayMapGroupIndex,
                    templateTypeName,
                }),
            );
            return false;
        }

        const duplicateProcessingOrders = translationMaps
            .map(translationMap => translationMap.processingOrder)
            .filter(
                (processingOrder, index, array) =>
                    array.indexOf(processingOrder) !== index,
            );

        if (duplicateProcessingOrders.length > 0) {
            Toast.error(
                getText('msg_9203e5f4c159', {
                    mapGroupIndex: displayMapGroupIndex,
                    templateTypeName,
                }),
            );
            return false;
        }

        const elseMaps = translationMaps.filter(
            translationMap => !!translationMap.else,
        );

        if (elseMaps.length > 1) {
            Toast.error(
                getText('msg_a59f3c5a8dc4', {
                    mapGroupIndex: displayMapGroupIndex,
                    templateTypeName,
                }),
            );
            return false;
        }

        if (elseMaps.length === 1) {
            const elseProcessingOrder = elseMaps[0].processingOrder ?? 0;
            const greatestProcessingOrder = Math.max(
                ...translationMaps.map(
                    translationMap => translationMap.processingOrder ?? 0,
                ),
            );

            if (elseProcessingOrder !== greatestProcessingOrder) {
                Toast.error(
                    getText('msg_6f8bb5b21f67', {
                        mapGroupIndex: displayMapGroupIndex,
                        templateTypeName,
                    }),
                );
                return false;
            }
        }

        for (const translationMap of translationMaps) {
            if (translationMap.else) {
                continue;
            }

            if (!getTrimmedValue(translationMap.from)) {
                Toast.error(
                    getText('msg_9133c97e9a71', {
                        mapGroupIndex: displayMapGroupIndex,
                        templateTypeName,
                    }),
                );
                return false;
            }

            if (!getTrimmedValue(translationMap.to)) {
                Toast.error(
                    getText('msg_d1b4e440631a', {
                        mapGroupIndex: displayMapGroupIndex,
                        templateTypeName,
                    }),
                );
                return false;
            }
        }

        return true;
    };

    const validateFormulaMapGroup = (
        group,
        templateTypeName,
        mapGroupIndex,
    ) => {
        const formulaMap = group?.formulaMap;
        const displayMapGroupIndex = mapGroupIndex + 1;

        if (!formulaMap) {
            Toast.error(
                getText('msg_a314f605d26a', {
                    mapGroupIndex: displayMapGroupIndex,
                    templateTypeName,
                }),
            );
            return false;
        }

        const evaluations = formulaMap.evaluations || [];

        if (!evaluations.length) {
            Toast.error(
                getText('msg_b4250716e37b', {
                    mapGroupIndex: displayMapGroupIndex,
                    templateTypeName,
                }),
            );
            return false;
        }

        for (const evaluation of evaluations) {
            if (!evaluation?.operatorId) {
                Toast.error(
                    getText('msg_c5361827f48c', {
                        mapGroupIndex: displayMapGroupIndex,
                        templateTypeName,
                    }),
                );
                return false;
            }

            const leftExpression = getTrimmedValue(evaluation.leftExpression);
            const rightExpression = getTrimmedValue(evaluation.rightExpression);

            const hasLeftExpression = leftExpression.length > 0;
            const hasRightExpression = rightExpression.length > 0;
            const isAlwaysOperator =
                evaluation.operatorId === ALWAYS_COMPARISON_OPERATOR_ID;

            if (
                !isAlwaysOperator &&
                (!hasLeftExpression || !hasRightExpression)
            ) {
                Toast.error(
                    getText('msg_d6472938059d', {
                        mapGroupIndex: displayMapGroupIndex,
                        templateTypeName,
                    }),
                );
                return false;
            }
        }

        return true;
    };

    const validateImportMapGroupsForTemplateType = (
        templateType,
        index = null,
    ) => {
        const templateTypeName = getTemplateTypeDisplayName(
            templateType,
            index,
        );
        const templateTypeLabel = getQuotedTemplateTypeLabel(
            templateType,
            index,
        );
        const importMapGroups = templateType?.importMapGroups || [];

        if (!importMapGroups.length) {
            return true;
        }

        const invalidGroupProcessingOrder = importMapGroups.some(
            importMapGroup => (importMapGroup.processingOrder ?? 0) <= 0,
        );

        if (invalidGroupProcessingOrder) {
            Toast.error(
                getText('msg_e7583a4916ae', {
                    templateTypeLabel,
                }),
            );
            return false;
        }

        const duplicateGroupProcessingOrders = importMapGroups
            .map(importMapGroup => importMapGroup.processingOrder)
            .filter(
                (processingOrder, groupIndex, array) =>
                    array.indexOf(processingOrder) !== groupIndex,
            );

        if (duplicateGroupProcessingOrders.length > 0) {
            Toast.error(
                getText('msg_f8694b5a27bf', {
                    templateTypeLabel,
                }),
            );
            return false;
        }

        for (
            let mapGroupIndex = 0;
            mapGroupIndex < importMapGroups.length;
            mapGroupIndex++
        ) {
            const group = importMapGroups[mapGroupIndex];
            const displayMapGroupIndex = mapGroupIndex + 1;

            if (!group?.importFieldId) {
                Toast.error(
                    getText('msg_0a7a5c6b38c0', {
                        mapGroupIndex: displayMapGroupIndex,
                        templateTypeLabel,
                    }),
                );
                return false;
            }

            if (
                group.importMapGroupTypeId !==
                    IMPORT_MAP_GROUP_TYPE.TRANSLATION &&
                group.importMapGroupTypeId !== IMPORT_MAP_GROUP_TYPE.FORMULA
            ) {
                Toast.error(
                    getText('msg_1b8b6d7c49d1', {
                        mapGroupIndex: displayMapGroupIndex,
                        templateTypeLabel,
                    }),
                );
                return false;
            }

            if (
                group.importMapGroupTypeId === IMPORT_MAP_GROUP_TYPE.TRANSLATION
            ) {
                if (group.formulaMap) {
                    Toast.error(
                        getText('msg_2c9c7e8d5ae2', {
                            mapGroupIndex: displayMapGroupIndex,
                            templateTypeLabel,
                        }),
                    );
                    return false;
                }

                if (
                    !validateTranslationMapGroup(
                        group,
                        templateTypeName,
                        mapGroupIndex,
                    )
                ) {
                    return false;
                }
            }

            if (group.importMapGroupTypeId === IMPORT_MAP_GROUP_TYPE.FORMULA) {
                if ((group.translationMaps || []).length > 0) {
                    Toast.error(
                        getText('msg_3dad8f9e6bf3', {
                            mapGroupIndex: displayMapGroupIndex,
                            templateTypeLabel,
                        }),
                    );
                    return false;
                }

                if (
                    !validateFormulaMapGroup(
                        group,
                        templateTypeName,
                        mapGroupIndex,
                    )
                ) {
                    return false;
                }
            }
        }

        return true;
    };

    const validateAllTemplateTypeMappings = templateTypesToValidate => {
        for (let index = 0; index < templateTypesToValidate.length; index++) {
            if (
                !validateImportMapGroupsForTemplateType(
                    templateTypesToValidate[index],
                    index,
                )
            ) {
                return false;
            }
        }

        return true;
    };

    const validateDefineImportTypesTab = () => {
        const templateTypes = getOrderedTemplateTypes(
            templateData?.templateTypes || [],
        );

        const nonBlankTemplateTypes = getSanitizedTemplateTypes(templateTypes);

        if (!nonBlankTemplateTypes.length) {
            Toast.error(getText('msg_43005ed92215'));
            return false;
        }

        const partialTemplateTypeIndex = templateTypes.findIndex(templateType =>
            isTemplateTypePartiallyCompleted(templateType),
        );

        if (partialTemplateTypeIndex !== -1) {
            Toast.error(
                getText('msg_744f7512f20d', {
                    index: partialTemplateTypeIndex + 1,
                }),
            );
            return false;
        }

        const duplicateProcessingOrders = nonBlankTemplateTypes
            .map(templateType => templateType.processingOrder)
            .filter((value, index, array) => array.indexOf(value) !== index);

        if (duplicateProcessingOrders.length > 0) {
            Toast.error(getText('msg_e38c213f79c9'));
            return false;
        }

        const demographicType = importTypes.find(
            type => type.id === ImportType.Demographic,
        );

        if (nonBlankTemplateTypes.length > 1 && demographicType) {
            const containsDemographic = nonBlankTemplateTypes.some(
                templateType =>
                    templateType.importTypeId === demographicType.id,
            );

            if (
                containsDemographic &&
                nonBlankTemplateTypes[0].importTypeId !== demographicType.id
            ) {
                Toast.error(getText('msg_32c3514a5b72'));
                return false;
            }
        }

        return true;
    };

    const setSelectedTemplateType = updatedTemplateTypeOrUpdater => {
        setTemplateData(prevData => {
            const orderedTemplateTypes = getOrderedTemplateTypes(
                prevData.templateTypes || [],
            );
            if (!orderedTemplateTypes.length) return prevData;

            const currentTemplateType =
                orderedTemplateTypes[selectedTemplateTypeIndex] ||
                orderedTemplateTypes[0];

            const updatedTemplateType =
                typeof updatedTemplateTypeOrUpdater === 'function'
                    ? updatedTemplateTypeOrUpdater(currentTemplateType)
                    : updatedTemplateTypeOrUpdater;

            const updatedTemplateTypes = orderedTemplateTypes.map(
                (templateType, index) =>
                    index === selectedTemplateTypeIndex
                        ? updatedTemplateType
                        : templateType,
            );

            const nextData = {
                ...prevData,
                templateTypes: updatedTemplateTypes,
            };

            return {
                ...nextData,
                columnPositionCount: calculateColumnPositionCount(nextData),
            };
        });
    };

    const handleEditFieldsForTemplateType = index => {
        if (!validateDefineFileTab()) {
            setActiveTab(TABS.DEFINE_FILE);
            return;
        }

        const orderedTemplateTypes = getOrderedTemplateTypes(
            templateData?.templateTypes || [],
        );
        const targetTemplateType = orderedTemplateTypes[index];

        if (!targetTemplateType?.importTypeId) {
            Toast.error(getText('msg_056a545aac76'));
            return;
        }

        setSelectedTemplateTypeIndex(index);
        setActiveTab(TABS.DEFINE_FIELDS);
    };

    const handleEditMappingForTemplateType = index => {
        if (!validateDefineFileTab()) {
            setActiveTab(TABS.DEFINE_FILE);
            return;
        }

        const orderedTemplateTypes = getOrderedTemplateTypes(
            templateData?.templateTypes || [],
        );
        const targetTemplateType = orderedTemplateTypes[index];

        if (!targetTemplateType?.importTypeId) {
            Toast.error(getText('msg_1c18d1a3df04'));
            return;
        }

        const hasDefinedFields = (targetTemplateType.templateFields || []).some(
            field => field.importFieldId,
        );

        if (!hasDefinedFields) {
            Toast.error(getText('msg_903d5df3957e'));
            return;
        }

        setSelectedTemplateTypeIndex(index);
        setActiveTab(TABS.DEFINE_MAPPING);
    };

    const handleNext = () => {
        if (activeTab === TABS.DEFINE_FILE) {
            if (!validateDefineFileTab()) {
                return;
            }

            setActiveTab(TABS.DEFINE_IMPORT_TYPES);
        } else if (activeTab === TABS.DEFINE_FIELDS) {
            if (!validateDefineFieldsTab()) {
                return;
            }

            setActiveTab(TABS.DEFINE_IMPORT_TYPES);
        } else if (activeTab === TABS.DEFINE_MAPPING) {
            setActiveTab(TABS.DEFINE_IMPORT_TYPES);
        }
    };

    const handleTabChange = tab => {
        if (activeTab === TABS.DEFINE_FILE) {
            if (!validateDefineFileTab()) {
                return;
            }
        } else if (activeTab === TABS.DEFINE_IMPORT_TYPES) {
            if (tab === TABS.DEFINE_FILE) {
                return setActiveTab(tab);
            }

            if (!validateDefineImportTypesTab()) {
                return;
            }

            setTemplateData(prevData => {
                const sanitizedTemplateTypes = getSanitizedTemplateTypes(
                    prevData.templateTypes || [],
                );

                return {
                    ...prevData,
                    templateTypes:
                        sanitizedTemplateTypes.length > 0
                            ? sanitizedTemplateTypes
                            : [createBlankTemplateType(1)],
                };
            });
        } else if (activeTab === TABS.DEFINE_FIELDS) {
            if (tab === TABS.DEFINE_FILE || tab === TABS.DEFINE_IMPORT_TYPES) {
                return setActiveTab(tab);
            }

            if (!validateDefineFieldsTab()) {
                return;
            }
        } else if (activeTab === TABS.DEFINE_MAPPING) {
            if (
                tab === TABS.DEFINE_FILE ||
                tab === TABS.DEFINE_IMPORT_TYPES ||
                tab === TABS.DEFINE_FIELDS
            ) {
                return setActiveTab(tab);
            }
        }

        setActiveTab(tab);
    };

    const handleSave = async () => {
        if (isSubmitting) {
            return;
        }

        if (!validateDefineFileTab()) {
            return;
        }

        if (!validateDefineImportTypesTab()) {
            return;
        }

        const sanitizedTemplateTypes = getSanitizedTemplateTypes(
            templateData.templateTypes || [],
        );

        if (!validateAllTemplateTypeFields(sanitizedTemplateTypes)) {
            return;
        }

        if (!validateAllTemplateTypeMappings(sanitizedTemplateTypes)) {
            return;
        }

        const payload = buildTemplatePayload(templateData, {
            preserveExistingRootImportTypeId: !!templateId,
        });

        try {
            setIsSubmitting(true);
            const savedTemplate = await onAddEditTemplate(payload);

            if (savedTemplate) {
                setTemplateData(prevData =>
                    syncTemplateDataWithSavedTemplate(
                        prevData,
                        savedTemplate,
                        importTypes,
                    ),
                );
            }

            setActiveTab(TABS.DEFINE_IMPORT_TYPES);
        } catch (error) {
            console.error('Error saving import template:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        onReturnToList();
    };

    if (error) {
        return <p>{error}</p>;
    }

    return (
        <div className={`${styles.container}`}>
            <div className={`card ${styles.card}`}>
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center border-bottom position-relative pb-3">
                        <h2 className="page-title float-start ms-4 m-0 col-2">
                            {activeTab === TABS.DEFINE_FIELDS ||
                            activeTab === TABS.DEFINE_MAPPING
                                ? getImportTypeEditorTitle(
                                      templateData,
                                      selectedTemplateType,
                                      importTypes,
                                  )
                                : (templateData?.name || '').trim() ||
                                  getText('msg_aaed86679f0b')}
                        </h2>
                        <ul className={`${styles.tabList} col-auto`}>
                            {[TABS.DEFINE_FILE, TABS.DEFINE_IMPORT_TYPES].map(
                                (tab, index) => (
                                    <React.Fragment key={tab}>
                                        <li
                                            className={`mx-3 ${styles.tabItem} ${isTopTabActive(tab) ? styles.active : ''} ${isSubmitting ? styles.disabled : ''}`}
                                            onClick={() => {
                                                if (!isSubmitting) {
                                                    handleTabChange(tab);
                                                }
                                            }}
                                        >
                                            {TAB_LABELS[tab]}
                                        </li>
                                        {index < 1 && (
                                            <i className="bi bi-chevron-right mx-2" />
                                        )}
                                    </React.Fragment>
                                ),
                            )}
                        </ul>

                        <div className="col-2">
                            <span
                                type="button"
                                onClick={() => {
                                    if (!isSubmitting) {
                                        handleCancel();
                                    }
                                }}
                                className="ing-text-buttons mx-3 ms-3 me-4 float-end"
                                style={{
                                    opacity: isSubmitting ? 0.6 : 1,
                                    pointerEvents: isSubmitting
                                        ? 'none'
                                        : 'auto',
                                }}
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_77dfd2135f4d')}
                            </span>
                        </div>
                    </div>

                    <div className="table-responsive-xxl w-100">
                        {isLoading ? (
                            <Spinner
                                width={50}
                                className={styles.loadingSpinner}
                            />
                        ) : (
                            <>
                                {activeTab === TABS.DEFINE_FILE && (
                                    <DefineFileTab
                                        templateData={templateData}
                                        setTemplateData={setTemplateData}
                                    />
                                )}
                                {activeTab === TABS.DEFINE_IMPORT_TYPES && (
                                    <DefineImportTypesTab
                                        templateData={templateData}
                                        setTemplateData={setTemplateData}
                                        onEditFields={
                                            handleEditFieldsForTemplateType
                                        }
                                        onEditMapping={
                                            handleEditMappingForTemplateType
                                        }
                                        isSubmitting={isSubmitting}
                                    />
                                )}
                                {activeTab === TABS.DEFINE_FIELDS && (
                                    <DefineFieldsTab
                                        templateData={templateData}
                                        setTemplateData={setTemplateData}
                                        selectedTemplateType={
                                            selectedTemplateType
                                        }
                                        setSelectedTemplateType={
                                            setSelectedTemplateType
                                        }
                                    />
                                )}
                                {activeTab === TABS.DEFINE_MAPPING && (
                                    <DefineMappingTab
                                        templateData={templateData}
                                        setTemplateData={setTemplateData}
                                        selectedTemplateType={
                                            selectedTemplateType
                                        }
                                        setSelectedTemplateType={
                                            setSelectedTemplateType
                                        }
                                    />
                                )}

                                <div className="d-flex justify-content-center mt-4">
                                    {activeTab === TABS.DEFINE_FILE && (
                                        <div className="col-12 col-xs-12 col-md-12 col-lg-6 m-auto">
                                            <span>
                                                <span className="text-danger">
                                                    *
                                                </span>{' '}
                                                {getText('msg_7c118f042eaa')}
                                            </span>
                                            <button
                                                type="button"
                                                className="float-end button-base ing-button-primary-left ms-3 me-4"
                                                onClick={handleNext}
                                                disabled={isSubmitting}
                                            >
                                                {getText('msg_bc981983e7f5')}
                                            </button>
                                        </div>
                                    )}

                                    {(activeTab === TABS.DEFINE_IMPORT_TYPES ||
                                        activeTab === TABS.DEFINE_FIELDS ||
                                        activeTab === TABS.DEFINE_MAPPING) && (
                                        <div
                                            className={`col-12 col-xs-12 col-md-12 col-lg-10 m-auto d-flex align-items-center ${
                                                isChildEditorTab(activeTab)
                                                    ? 'justify-content-between'
                                                    : 'justify-content-end'
                                            }`}
                                        >
                                            {hasFullAccess && (
                                                <button
                                                    type="button"
                                                    className="button-base ing-button-primary-left ms-3 me-4"
                                                    onClick={handleSave}
                                                    disabled={isSubmitting}
                                                >
                                                    {isSubmitting
                                                        ? getText(
                                                              'msg_ae7e887517b0',
                                                          )
                                                        : getText(
                                                              'msg_efc007a393f6',
                                                          )}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

AddEditImportTemplateForm.propTypes = {
    onAddEditTemplate: PropTypes.func.isRequired,
    onReturnToList: PropTypes.func.isRequired,
    templateId: PropTypes.string,
};

export default AddEditImportTemplateForm;
